import { useState } from 'react';
import { MapPin, ExternalLink, Coffee, Store, Instagram, Facebook } from 'lucide-react';

interface GuideItem {
  id: string;
  title: string;
  description: string;
  category: 'RESTAURANT' | 'BEACH' | 'ATTRACTION' | 'SHOP';
  location: {
    address: string;
    lat: number;
    lng: number;
    mapUrl?: string;
  };
  image?: string;
  links?: {
    tripAdvisor?: string;
    instagram?: string;
    facebook?: string;
    linktree?: string;
  };
  mapEmbed?: string;
}

const LocalGuide = () => {
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  const guideItems: GuideItem[] = [
    {
      id: '1',
      title: 'Tratamento',
      description: "Don't miss the local favorite: handmade gelato, fresh cones, crêpes and waffles in the heart of Apollonia.",
      category: 'RESTAURANT',
      location: {
        address: 'Address / directions',
        lat: 36.97397885776658,
        lng: 24.721514412597024,
        mapUrl: 'https://maps.app.goo.gl/3Cmr5rUaQxiee83C9'
      },
      image: '/images/tratamento.jpg',
      links: {
        tripAdvisor: 'https://www.tripadvisor.com.gr/Restaurant_Review-g2721146-d12741682-Reviews-Tratamento-Apollonia_Sifnos_Cyclades_South_Aegean.html',
        instagram: 'https://www.instagram.com/tratamento.sifnos/',
        facebook: 'https://www.facebook.com/tratamento.sifnos/',
        linktree: 'https://linktr.ee/tratamento.sifnos'
      },
      mapEmbed: '<iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3187.500746051752!2d24.721514412597024!3d36.97397885776658!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14988d52d2a46903%3A0x1589c11e43ce3ef9!2sTratamento!5e0!3m2!1sen!2sgr!4v1791147217950!5m2!1sen!2sgr" width="100%" height="300" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>'
    },
  ];

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'RESTAURANT':
        return <Coffee className="w-5 h-5 text-rizes-olive" />;
      case 'BEACH':
        return <MapPin className="w-5 h-5 text-rizes-olive" />;
      case 'ATTRACTION':
        return <MapPin className="w-5 h-5 text-rizes-olive" />;
      case 'SHOP':
        return <Store className="w-5 h-5 text-rizes-olive" />;
      default:
        return <MapPin className="w-5 h-5 text-rizes-olive" />;
    }
  };

  return (
    <section id="local-guide" className="bg-white py-14 sm:py-16 lg:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 sm:px-6 lg:grid-cols-[minmax(16rem,0.75fr)_minmax(0,1.25fr)] lg:gap-12 lg:px-8">
        <header className="max-w-lg">
          <p className="mb-2 text-xs font-semibold uppercase text-rizes-olive">Around Apollonia</p>
          <h2 className="section-title">Our Local Favorites</h2>
          <p className="section-subtitle mx-0">
            Experience the best flavors of Sifnos with our top recommendations for sweets.
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6">
          {guideItems.map(item => (
            <article key={item.id} className="grid overflow-hidden rounded-lg border border-rizes-limestone bg-rizes-ivory md:grid-cols-[0.9fr_1.1fr]">
              {item.image && !failedImages[item.id] ? (
                <div className="aspect-[4/3] overflow-hidden md:aspect-auto md:min-h-[22rem]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-500 ease-out hover:scale-[1.025] motion-reduce:transition-none motion-reduce:hover:scale-100"
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailedImages((previous) => ({ ...previous, [item.id]: true }))}
                  />
                </div>
              ) : item.mapEmbed ? (
                <div className="min-h-64 w-full md:min-h-[22rem]" dangerouslySetInnerHTML={{ __html: item.mapEmbed }} />
              ) : null}
              <div className="flex flex-col justify-center p-6 sm:p-8">
                <div className="mb-2 flex items-center space-x-2">
                  {getCategoryIcon(item.category)}
                  <h3 className="font-serif text-2xl text-rizes-ink">{item.title}</h3>
                </div>
                <p className="mb-5 leading-7 text-gray-600">{item.description}</p>

                <div className="mb-4 flex items-center space-x-2">
                  {item.location.mapUrl ? (
                    <a
                      href={item.location.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-800"
                    >
                      <MapPin className="w-4 h-4" />
                      <span>{item.location.address}</span>
                    </a>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">{item.location.address}</span>
                    </>
                  )}
                </div>

                {item.links && (
                  <div className="flex space-x-4">
                    {item.links.tripAdvisor && (
                      <a
                        href={item.links.tripAdvisor}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="TripAdvisor"
                        title="TripAdvisor"
                        className="inline-flex items-center justify-center rounded p-2 text-primary-600 hover:bg-gray-100 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                      >
                        <ExternalLink className="w-5 h-5" aria-hidden="true" />
                      </a>
                    )}
                    {item.links.instagram && (
                      <a
                        href={item.links.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        title="Instagram"
                        className="inline-flex items-center justify-center rounded p-2 text-primary-600 hover:bg-gray-100 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                      >
                        <Instagram className="w-5 h-5" aria-hidden="true" />
                      </a>
                    )}
                    {item.links.facebook && (
                      <a
                        href={item.links.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        title="Facebook"
                        className="inline-flex items-center justify-center rounded p-2 text-primary-600 hover:bg-gray-100 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                      >
                        <Facebook className="w-5 h-5" aria-hidden="true" />
                      </a>
                    )}
                    {item.links.linktree && (
                      <a
                        href={item.links.linktree}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Linktree"
                        title="Linktree"
                        className="inline-flex items-center justify-center rounded p-2 text-primary-600 hover:bg-gray-100 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600"
                      >
                        <ExternalLink className="w-5 h-5" aria-hidden="true" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LocalGuide;